import { Button, Stack, TableSection, Text } from '@solvhealth/jigsaw-v1';
import isEmpty from 'lodash/isEmpty';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { imageDataClear } from '~/actions/imageData';
import { locationDoesntArbitrarilyBlockDownloaderDriver } from '~/components/DownloadDriver/util';
import { useIsSolvPayV2Enabled } from '~/components/Payments/util';
import { colors } from '~/constants/colors';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { RemixRoutes } from '~/core/remix';
import { isTelemedBooking } from '~/core/util/booking';
import { isPaperworkCompleted } from '~/core/util/paperwork';
import { isPaymentMethodAddedForCurrentBooking } from '~/core/util/payment';
import { getPreVisitPaymentRoute } from '~/core/util/videoVisit';
import { resetPhotoId } from '~/ducks/photoId';
import { usePalette } from '~/hooks/usePalette';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import {
  IS_COMING_FROM_ACCOUNT_COOKIE_NAME,
  IS_COMING_FROM_HANDOFF_COOKIE_NAME,
} from '../../../../config/index';
import { analyticsTrackEvent } from '../../../../core/analytics';
import {
  ACTION_LOAD,
  campaignNames,
  PAPERWORK_COMPLETE_SECTION,
  PAPERWORK_JUMP_TO_SECTION,
  PAPERWORK_SIGN_UP_CLICK,
} from '../../../../core/analytics/events';
import history from '../../../../core/history';
import withAuthentication from '../../../../core/login/withAuthentication';
import { doesCookieExist, setCookie, unsetCookie } from '../../../../core/util/cookies';
import {
  isPartOfIntellivisitGroup,
  isPaymentsEnabled,
  isProviderGroupLocation,
  isSolvExpress,
  isTelemedLocation,
  isWaitlistSmsDisabled,
} from '../../../../core/util/location';
import { useQueryFamilyBookings } from '../../../../hooks/useQueryFamilyBookings';
import useSetTitle from '../../../../hooks/useSetTitle';
import { validateFamilyBooking } from '../../../../util/validateFamilyBooking';
import DownloadDriver from '../../../DownloadDriver';
import PaperworkLayout from '../../PaperworkLayout';
import { SectionNumber } from '../../constants';
import { useInitializePaperworkData } from '../../hooks';
import { PaperworkFieldName, PaperworkSectionRoute } from '../../paperworkConstants';
import { PGSectionNumber } from '../../providerGroups/constants';
import { CheckmarkSvg } from './CheckmarkSvg';

const title = 'Paperwork – Complete';

type NextRouteCtaProps = {
  onClick: () => void;
  children: ReactNode;
};

const NextRouteCta = ({ onClick, children }: NextRouteCtaProps) => (
  <Button fullWidth onClick={onClick} size="large" variant="text">
    {children}
  </Button>
);

const CHECK_MARK_ICON = '/images/icons/checkmark_crunchberry_inverted.svg';
const X_ICON = '/images/icons/x/small_grey.svg';

const StyledIconContainer = styled.div.withConfig({ componentId: 'sc-mxk9bo-0' })`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  margin-right: 8px;
`;

const StyledIcon = styled.img.withConfig({ componentId: 'sc-mxk9bo-1' })`
  width: 12px;
`;

const StyledBookingRow = styled.div.withConfig({ componentId: 'sc-mxk9bo-2' })`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledLink = styled.a.withConfig({ componentId: 'sc-mxk9bo-3' })`
  color: ${colors.gray600} !important;
  text-decoration: underline;
`;

const StyledStartPaperworkButton = styled(Button).withConfig({ componentId: 'sc-mxk9bo-4' })`
  margin: 16px 0px 32px 0px !important;
`;

const StyledAllSet = styled(Text).withConfig({ componentId: 'sc-mxk9bo-5' })`
  margin: 32px 0px !important;
`;

const StyledFamilyBookingsText = styled(Text).withConfig({ componentId: 'sc-mxk9bo-6' })`
  margin: 16px 0px !important;
`;

type Props = {
  fetchBooking?: (...args: any[]) => any;
  fetchLocation: (...args: any[]) => any;
  location?: any;
  paperwork?: any;
  openSignUpModal?: (...args: any[]) => any;
  isLoggedIn?: boolean;
  loggingIn?: boolean;
  isAccountSummaryLoading?: boolean;
  preloadedData?: any;
  booking?: any;
};

export const Complete = (props: Props) => {
  const {
    paperwork,
    fetchBooking,
    fetchLocation,
    preloadedData,
    location,
    loggingIn,
    isLoggedIn,
    isAccountSummaryLoading,
    openSignUpModal,
    booking,
  } = props;

  const bookingId = usePaperworkContextBookingId();
  const dispatch = useDispatch();

  const { t } = useTranslation(['paperwork']);
  const { familyId, isFamilyBooking } = validateFamilyBooking(booking);
  const { bookingsInFamily, primaryBooking } = useQueryFamilyBookings(familyId);

  useInitializePaperworkData(paperwork, bookingId, location);

  const { intellivisitPaperworkCta } = useLDFlags();

  const StyledIntellivisitText = styled(Text).withConfig({ componentId: 'sc-mxk9bo-7' })`
    margin: 32px 0 48px 0 !important;
  `;

  const [isComingFromHandoff, setIsComingFromHandoff] = useState(
    doesCookieExist(IS_COMING_FROM_HANDOFF_COOKIE_NAME)
  );

  const [isComingFromAccount, setIsComingFromAccount] = useState(
    doesCookieExist(IS_COMING_FROM_ACCOUNT_COOKIE_NAME)
  );

  /**
   * If the IS_COMING_FROM_HANDOFF_COOKIE_NAME is set, it will be unset upon landing on this page (see
   * useEffect below). But if Mary goes back in to edit paperwork using either the back arrow or the
   * "edit paperwork" cta, when she submits it again we want to preserve the handoff cookie so that
   * the usePushToPaperworkComplete behavior and this (Complete) page behavior is what is expected
   * for handoff. This wasComingFromHandoff value is used to conditionally reset the cookie upon
   * clicking edit paperwork or the back button (which goes back to the /finalize page)
   */
  const wasComingFromHandoff = useRef(isComingFromHandoff);

  const onGoBack = () => {
    if (wasComingFromHandoff.current) {
      setCookie(IS_COMING_FROM_HANDOFF_COOKIE_NAME);
    }
    history.goBack();
  };

  useEffect(() => {
    unsetCookie(IS_COMING_FROM_HANDOFF_COOKIE_NAME);
    unsetCookie(IS_COMING_FROM_ACCOUNT_COOKIE_NAME);
    analyticsTrackEvent(PAPERWORK_COMPLETE_SECTION, {
      action: ACTION_LOAD,
      isComingFromHandoff,
      bookingId,
      locationId: location && location.id,
      isLoggedIn,
    });
    if (!booking) {
      fetchBooking!(bookingId);
    }

    if (booking && isEmpty(location)) {
      fetchLocation(booking.locationId ?? booking.location_id);
    }
  }, [booking, bookingId, fetchBooking, isComingFromHandoff, location]);
  useSetTitle(title);
  const isProviderGroup = isProviderGroupLocation(location);

  const handleButtonClick = (nextRoute: any) => {
    analyticsTrackEvent(PAPERWORK_JUMP_TO_SECTION, {
      from: `${PaperworkSectionRoute.COMPLETE}/${bookingId}`,
      to: nextRoute,
      bookingId,
      isLoggedIn,
    });

    history.push(nextRoute);
  };

  const renderNextRouteCta = () => {
    const userProfileId = preloadedData?.userProfile.user_profile_id;

    if (isComingFromAccount) {
      const nextRoute = userProfileId ? `/account/home/${userProfileId}/get-care` : '/account/home';
      const copy = isLoggedIn
        ? t('paperwork:complete.returnToAccount')
        : t('paperwork:complete.noThanks');

      return <NextRouteCta onClick={() => handleButtonClick(nextRoute)}>{copy}</NextRouteCta>;
    }

    if (
      !isProviderGroupLocation(location) &&
      !isSolvExpress(location) &&
      !isWaitlistSmsDisabled(location)
    ) {
      const copy = isLoggedIn
        ? t('paperwork:complete.viewWaitlist')
        : t('paperwork:complete.noThanks');
      const nextRoute =
        isLoggedIn && userProfileId
          ? `/account/home/${userProfileId}/manage?b=${bookingId}`
          : `/b/${bookingId}`;

      return <NextRouteCta onClick={() => handleButtonClick(nextRoute)}>{copy}</NextRouteCta>;
    }

    const copy = isLoggedIn
      ? t('paperwork:complete.viewVisitDetails')
      : t('paperwork:complete.noThanks');

    return (
      <NextRouteCta onClick={() => handleButtonClick(`/pass/${bookingId}`)}>{copy}</NextRouteCta>
    );
  };

  const handleSignUpClick = () => {
    analyticsTrackEvent(PAPERWORK_SIGN_UP_CLICK, {
      bookingId,
      locationId: location && location.id,
      providerId: booking && booking.providerId,
      isLoggedIn,
    });

    // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    openSignUpModal();
  };

  let subheader;
  if (isComingFromHandoff) {
    subheader = t('paperwork:complete.returnTablet');
  } else {
    subheader = isFamilyBooking
      ? t('paperwork:complete.paperworkReceivedPersonal', {
          name:
            paperwork.formData[PaperworkFieldName.PATIENT_FIELD_FIRST_NAME] ||
            preloadedData?.userProfile?.first_name ||
            preloadedData?.booking?.firstName ||
            booking.first_name,
        })
      : t('paperwork:complete.paperworkReceived');
  }

  const shouldRenderSignUpCta =
    !isAccountSummaryLoading &&
    !isComingFromHandoff &&
    !loggingIn &&
    !isLoggedIn &&
    !(isFamilyBooking && bookingsInFamily.some((booking) => !isPaperworkCompleted(booking)));

  const destinationRoute =
    isProviderGroup || isFamilyBooking
      ? `/pass/${isProviderGroup ? bookingId : primaryBooking?.id}`
      : `${PaperworkSectionRoute.FINALIZE}/${bookingId}`;

  const locationDoesntBlockDownloadDriver = locationDoesntArbitrarilyBlockDownloaderDriver(
    location?.id ?? booking?.locationId ?? booking?.location_id
  );

  const goToNextFamilyBookingPaperwork = () => {
    const nextBookingWithIncompletePaperwork = bookingsInFamily.find(
      (booking) => !isPaperworkCompleted(booking)
    );
    if (!nextBookingWithIncompletePaperwork || isEmpty(nextBookingWithIncompletePaperwork)) {
      return;
    }
    dispatch(imageDataClear());
    dispatch(resetPhotoId());
    handleButtonClick(`${PaperworkSectionRoute.START}/${nextBookingWithIncompletePaperwork.id}`);
  };

  const { palette } = usePalette();

  const familyBookingsRows = useMemo(
    () => (
      <div>
        {bookingsInFamily.map((booking) => (
          <StyledBookingRow key={booking.id}>
            <StyledIconContainer>
              {isPaperworkCompleted(booking) ? (
                <CheckmarkSvg color={palette.base} />
              ) : (
                <StyledIcon
                  alt="checkmark"
                  src={isPaperworkCompleted(booking) ? CHECK_MARK_ICON : X_ICON}
                />
              )}
            </StyledIconContainer>
            <Text>
              {booking.firstName} {booking.lastName.slice(0, 1)}.
            </Text>
            {isPaperworkCompleted(booking) && (
              <Text>
                &nbsp;-{' '}
                <StyledLink href={`${PaperworkSectionRoute.START}/${booking.id}`}>
                  {t('paperwork:complete.edit')}
                </StyledLink>
              </Text>
            )}
          </StyledBookingRow>
        ))}
      </div>
    ),
    [bookingsInFamily, t, palette]
  );

  const hasIncompleteFamilyBookingsPaperwork = bookingsInFamily.some(
    (booking) => !isPaperworkCompleted(booking)
  );

  const { isSolvPayV2Enabled, isSolvPayV2StatusLoading } = useIsSolvPayV2Enabled(location.id);

  const editPaymentRoute = useMemo(() => {
    if (!booking) {
      return {};
    }

    if (isTelemedBooking(booking) || isTelemedLocation(location)) {
      return { href: getPreVisitPaymentRoute(booking), external: false };
    }

    if (isSolvPayV2Enabled) {
      return { href: RemixRoutes.payments.addCard(bookingId), external: true };
    }

    return { href: `/payments/add-card/r/pass/${booking.id}`, external: false };
  }, [booking, bookingId, isSolvPayV2Enabled, location]);

  // const editPaymentRoute = (booking: Booking) => {
  //   if (isTelemedBooking(booking) || isTelemedLocation(location)) {
  //     return getPreVisitPaymentRoute(booking);
  //   }

  //   waitForSolvPayVersionAndRedirect();
  //   return;
  // };

  return (
    <>
      {shouldRenderSignUpCta && locationDoesntBlockDownloadDriver && (
        <DownloadDriver campaignName={campaignNames.END_OF_PAPERWORK} variant="rebooking" />
      )}
      <PaperworkLayout
        onGoBack={onGoBack}
        section={
          isProviderGroup
            ? PGSectionNumber.VerificationAndReview
            : SectionNumber.VerificationAndReview
        }
        table
      >
        <TableSection>
          <Stack alignItems="center" space={2} textAlign="center">
            <img
              alt="Paperwork Complete"
              height={136}
              src="/images/paperwork/urgent-care-green-2@2x.svg"
              width={129}
            />

            <Stack space={1}>
              {isPartOfIntellivisitGroup(location) && intellivisitPaperworkCta ? (
                <>
                  <Text variant="h2">{t('paperwork:complete.intellivisit.almostDone')}</Text>
                  <StyledIntellivisitText>
                    {t('paperwork:complete.intellivisit.pleaseComplete')}
                  </StyledIntellivisitText>
                  <Stack alignItems="stretch" alignSelf="stretch" space={1} width="100%">
                    <Button
                      fullWidth
                      onClick={() =>
                        (window.location.href = `https://solv.ivstagingnav.com/start?secret=d9c772b9-f8af-458f-802e-54a151adb411&BookingID=${bookingId}`)
                      }
                      size="large"
                    >
                      {t('paperwork:complete.intellivisit.completeQuestionnaire')}
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <StyledAllSet variant="h2">{t('paperwork:complete.allSet')}</StyledAllSet>
                  <Text>{subheader}</Text>
                  {isFamilyBooking && hasIncompleteFamilyBookingsPaperwork && (
                    <StyledFamilyBookingsText>
                      {t('paperwork:complete.continueCompletingPaperwork')}
                    </StyledFamilyBookingsText>
                  )}
                </>
              )}
            </Stack>
            {!(isPartOfIntellivisitGroup(location) && intellivisitPaperworkCta) &&
              (isFamilyBooking ? (
                <Stack alignItems="stretch" alignSelf="stretch" space={1}>
                  {familyBookingsRows}
                  {hasIncompleteFamilyBookingsPaperwork && (
                    <StyledStartPaperworkButton fullWidth onClick={goToNextFamilyBookingPaperwork}>
                      {t('paperwork:start.begin.cta')}
                    </StyledStartPaperworkButton>
                  )}
                  <Button
                    fullWidth
                    href={destinationRoute}
                    variant="text"
                    {...(!isLoggedIn && !isAccountSummaryLoading ? { py: 0 } : {})}
                  >
                    {t('paperwork:complete.visitDetails')}
                  </Button>
                </Stack>
              ) : (
                <Stack alignItems="stretch" alignSelf="stretch" space={1}>
                  {isLoggedIn && renderNextRouteCta()}
                  <Button
                    fullWidth
                    href={destinationRoute}
                    onClick={() => {
                      if (
                        destinationRoute === `${PaperworkSectionRoute.FINALIZE}/${bookingId}` &&
                        wasComingFromHandoff.current
                      ) {
                        setCookie(IS_COMING_FROM_HANDOFF_COOKIE_NAME);
                      }
                    }}
                    variant="text"
                    {...(!isLoggedIn && !isAccountSummaryLoading ? { py: 0 } : {})}
                  >
                    {isProviderGroup
                      ? t('paperwork:complete.visitDetails')
                      : t('paperwork:complete.edit')}
                  </Button>
                </Stack>
              ))}
            {isPaymentsEnabled(location) && (
              <Stack alignItems="stretch" alignSelf="stretch" space={1}>
                <Button
                  fullWidth
                  {...editPaymentRoute}
                  variant="text"
                  {...(!isLoggedIn && !isAccountSummaryLoading ? { py: 0 } : {})}
                >
                  {`${
                    isPaymentMethodAddedForCurrentBooking(booking) ? 'Edit' : 'Add'
                  } credit card information`}
                </Button>
              </Stack>
            )}
          </Stack>
        </TableSection>
        {shouldRenderSignUpCta &&
          !(isPartOfIntellivisitGroup(location) && intellivisitPaperworkCta) && (
            <TableSection>
              <Stack alignItems="stretch" pt={1} space={2} textAlign="center">
                <Stack alignItems="center" space={1}>
                  <Text variant="h3">{t('paperwork:complete.upsell.title')}</Text>
                  <Text>
                    {t('paperwork:complete.upsell.trackSpending')}
                    <br />
                    {t('paperwork:complete.upsell.costEstimates')}
                  </Text>
                </Stack>

                <Stack alignItems="stretch" alignSelf="stretch" space={1} width="100%">
                  <Button fullWidth onClick={handleSignUpClick} size="large">
                    {t('paperwork:complete.upsell.viewMyBenefits')}
                  </Button>
                  {renderNextRouteCta()}
                </Stack>
              </Stack>
            </TableSection>
          )}
      </PaperworkLayout>
    </>
  );
};

export default withAuthentication(Complete);
